/// <reference types="google.maps" />

import React, { useState } from 'react';
import { Close } from '@sharefiledev/icons';
import { BillingInfo } from '@sharefiledev/sharefile-appshell';
import {
	AutoComplete,
	AutoCompleteProps,
	Button,
	Flex,
	Form,
	FormInstance,
	Input,
	Skeleton,
	theme,
	Typography,
} from 'antd';
import { useAsync } from 'react-async-hook';
import { t } from '../../../translate';
import { addressFieldId } from '../addressField';
import { getPredictions } from './getPredictions';
import { useGetGoogleServices } from './useGetGoogleServices';
import { getMapsApiKey, getPlaceDetails } from './utils';

export interface AddressAutoCompleteProps {
	onAddressSelected: (address: BillingInfo) => void;
	form: FormInstance;
	country?: string;
	testPrefixId?: string;
}

const { Text } = Typography;

export function AddressAutoComplete({
	onAddressSelected,
	form,
	country = 'US',
	testPrefixId = '',
}: AddressAutoCompleteProps) {
	const { token } = theme.useToken();
	const GOOGLE_API_KEY = getMapsApiKey();

	const [value, setValue] = useState<string>('');
	const [options, setOptions] = useState<AutoCompleteProps['options']>([]);
	const [isAutocompleteOpen, setIsAutocompleteOpen] = useState<boolean>(false);
	const [hasMadeSelection, setHasMadeSelection] = useState<boolean>(false);
	const [hasManualButtonBeenClicked, setHasManualButtonBeenClicked] =
		useState<boolean>(false);
	const [sessionToken, setSessionToken] = useState<
		google.maps.places.AutocompleteSessionToken | undefined
	>(undefined);

	const { loading, error, result: googleServices } = useGetGoogleServices(GOOGLE_API_KEY);

	useAsync(async () => {
		if (!sessionToken && googleServices?.sessionToken) {
			setSessionToken(new googleServices.sessionToken());
		}

		if (value.length < 3) {
			setIsAutocompleteOpen(false);
			return;
		}

		await getPredictions(
			value,
			country,
			predictions => {
				setOptions(predictions);
				setIsAutocompleteOpen(true);
			},
			token,
			googleServices?.autoCompleteService,
			sessionToken,
			hasManualButtonBeenClicked
		);
	}, [
		value,
		getPredictions,
		country,
		token,
		googleServices?.autoCompleteService,
		sessionToken,
		hasManualButtonBeenClicked,
		,
	]);

	const handleAddressSelection = (address: BillingInfo) => {
		form.setFieldsValue({
			[addressFieldId.addressLine1]: address.Address1,
			[addressFieldId.city]: address.City,
			[addressFieldId.country]: address.Country,

			[addressFieldId.state]: address.State,
			[addressFieldId.province]: address.State,

			[addressFieldId.zipCode]: address.Zip,
			[addressFieldId.postalCode]: address.Zip,
		});

		onAddressSelected(address);
	};

	const onSelect = (selectedValue: string) => {
		if (!googleServices) {
			return;
		}

		getPlaceDetails(
			googleServices.getPlaceDetails,
			selectedValue,
			handleAddressSelection,
			() => setIsAutocompleteOpen(false),
			sessionToken
		);
		setSessionToken(undefined); // Reset session token after selection
		setHasMadeSelection(true);
	};

	// GitHub Copilot: Worked with author to determine how to prevent dropdown from closing whenever clicked.
	const customizeDropdown = (OriginNode: React.ReactNode) => {
		return (
			<Flex
				vertical
				onClick={e => {
					e.stopPropagation();
					e.preventDefault();
				}}
				onMouseDown={e => {
					e.stopPropagation();
					e.preventDefault();
				}}
			>
				<Flex
					justify="space-between"
					align="center"
					style={{
						padding: `${token.paddingXXS}px 0 ${token.paddingXXS}px ${token.paddingSM}px`,
					}}
				>
					<Text
						style={{
							fontSize: token.fontSizeSM,
							color: token.colorTextSecondary,
						}}
					>
						{t('billing_address.suggestions_attribution')}
					</Text>
					<Button
						size="small"
						shape="circle"
						onClick={e => {
							e.stopPropagation();
							e.preventDefault();
							setIsAutocompleteOpen(false);
						}}
						onMouseDown={e => {
							e.stopPropagation();
							e.preventDefault();
						}}
						type="text"
					>
						<Close size={token.fontSizeXL} />
					</Button>
				</Flex>
				{OriginNode}
				<Flex
					onClick={e => {
						e.stopPropagation();
						e.preventDefault();
						setHasManualButtonBeenClicked(true);
						setIsAutocompleteOpen(false);
					}}
					onMouseDown={e => {
						e.stopPropagation();
						e.preventDefault();
					}}
					role="option"
					style={{
						backgroundColor: token.colorBgContainerDisabled,
						padding: `0 0 0 ${token.paddingSM}px`,
						alignItems: 'center',
						cursor: 'pointer',
						minHeight: token.controlHeight,
					}}
				>
					<Text underline>{t('billing_address.enter_address_manually')}</Text>
				</Flex>
			</Flex>
		);
	};

	if (loading) {
		return <Skeleton.Input active />;
	}
	if (error) {
		return (
			<Input
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
					onAddressSelected({ Address1: event.target.value });
				}}
				data-testid={`${testPrefixId}autocomplete-fallback-street-address`}
			/>
		);
	}

	return (
		<Form.Item
			name={addressFieldId.addressLine1}
			htmlFor={testPrefixId + addressFieldId.addressLine1}
			label={t('billing_address.street')}
			required
			id={
				testPrefixId
					? `${testPrefixId}-${addressFieldId.addressLine1}`
					: addressFieldId.addressLine1
			}
			rules={[
				{
					required: true,
					message: t('billing_address.validation_message.street_required'),
				},
			]}
		>
			<AutoComplete
				id={testPrefixId + addressFieldId.addressLine1}
				onChange={newValue => {
					setValue(newValue);
					setHasMadeSelection(false);
				}}
				onSelect={onSelect}
				options={options ?? []}
				style={{ width: '100%' }}
				allowClear={{
					clearIcon: (
						<Text style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
							{t('call_to_action.clear')}
						</Text>
					),
				}}
				onClear={() => {
					setIsAutocompleteOpen(false);
					setHasManualButtonBeenClicked(false);
				}}
				dropdownRender={customizeDropdown}
				virtual={false}
				open={!hasManualButtonBeenClicked && !hasMadeSelection && isAutocompleteOpen}
				data-testid={`${testPrefixId}address-autocomplete`}
				notFoundContent={
					<Text
						strong
						style={{
							padding: `${token.paddingXS}px 0`,
						}}
					>
						{t('billing_address.suggestions_not_found')}
					</Text>
				}
			/>
		</Form.Item>
	);
}
